import React, { useState } from 'react';
import MilanLogo from './icons/MilanLogo';

const faqData = [
    {
        question: "What is Milan's core philosophy?",
        answer: "Milan is designed for people looking for something real. By focusing on intentionality, we encourage deep, meaningful relationships rather than superficial scrolling."
    },
    {
        question: "How does the exclusive matching cycle work?",
        answer: "To prevent decision fatigue and promote genuine interaction, you can only have one active romantic relationship at a time on Milan. When you form a match, other pending invitations are paused."
    },
    {
        question: "What is the Slate Wipe?",
        answer: "If a match doesn't work out, unmatching resets your relationship state. We believe in fresh starts without baggage or endless backlogs of expired conversations."
    },
    {
        question: "How are AI reflections generated?",
        answer: "Our AI analyzes your answers to reflection questions to highlight compatibility patterns, communication styles, and shared values, providing insightful conversation starters."
    },
    {
        question: "How do I pause discovery?",
        answer: "You can toggle your visibility at any time in the app settings menu. Pausing discovery ensures you won't appear in new matching pools while you focus on other things."
    },
    {
        question: "How can I delete my account?",
        answer: "Account deletion is handled instantly within the app's settings under 'Delete Account'. Note that you cannot delete your account while you have an active match; you must unmatch first."
    }
];

const FAQItem = ({ question, answer, answerId, isOpen, onClick }) => {
    return (
        <div className="border-b border-border py-6 reveal">
            <button
                className="w-full flex justify-between items-center text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring group"
                onClick={onClick}
                aria-expanded={isOpen}
                aria-controls={answerId}
            >
                <span className={`text-xl sm:text-2xl font-semibold tracking-[-0.010em] transition-colors duration-200 ${isOpen ? 'text-accent' : 'text-foreground group-hover:text-accent'}`}>
                    {question}
                </span>
                <span className={`text-2xl ml-4 font-light transition-colors duration-200 ${isOpen ? 'text-accent' : 'text-muted-foreground group-hover:text-accent'}`}>
                    {isOpen ? '−' : '+'}
                </span>
            </button>
            <div
                id={answerId}
                hidden={!isOpen}
                className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                    }`}
            >
                <p className="text-lg leading-relaxed text-muted-foreground">
                    {answer}
                </p>
            </div>
        </div>
    );
};

const SupportPage = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <div className="brand-top-fade brand-top-fade--visible" aria-hidden="true" />
            <div className="brand-mark brand-mark--compact" role="img" aria-label="Milan">
                <MilanLogo className="brand-mark__logo" aria-hidden="true" />
            </div>
            <div className="bg-background min-h-screen">
                <div className="max-w-[800px] mx-auto px-5 sm:px-6 md:px-8 pt-28 pb-20 sm:pt-40 sm:pb-32">
                    <div className="reveal mb-16">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.020em] leading-[1.1] mb-6">
                        Support & FAQs
                    </h1>
                    <p className="text-lg sm:text-xl text-muted-foreground">
                        Find answers to common questions about Milan's features and philosophy.
                    </p>
                </div>

                <div className="space-y-2 mb-20">
                    {faqData.map((faq, index) => (
                        <FAQItem
                            key={index}
                            answerId={`faq-answer-${index}`}
                            question={faq.question}
                            answer={faq.answer}
                            isOpen={openIndex === index}
                            onClick={() => handleToggle(index)}
                        />
                    ))}
                </div>

                {/* Google Form Action Card */}
                <div className="reveal bg-secondary/50 rounded-2xl p-8 border border-border/80 text-center">
                    <h3 className="text-2xl font-semibold mb-3 text-foreground">
                        Still need help?
                    </h3>
                    <p className="text-base text-muted-foreground mb-6 max-w-[500px] mx-auto">
                        If you have questions, feedback, or need assistance, fill out our secure support form.
                    </p>
                    <a
                        href="https://forms.gle/6i97Vf5es4Qxyz5e7"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-8 py-4 bg-primary text-primary-foreground hover:bg-accent hover:text-white text-base font-semibold rounded-xl transition-all duration-200 shadow-sm"
                    >
                        Contact Support Form
                    </a>
                </div>
            </div>
        </div>
    </>
    );
};

export default SupportPage;
